import type { ReactNode } from 'react';
import { forwardRef, Fragment, useLayoutEffect, useRef } from 'react';
import { styled } from 'goober';

import { PopperComponent } from '@/components/popper/popper-component';

import { sliceOverflowPillItems } from './overflow-pill-rows.utils';
import { useOverflowPillLayout } from './use-overflow-pill-layout';

export interface OverflowPillRenderContext {
  index: number;
  isRevealedOnExpand: boolean;
}

export interface OverflowPillExpandControlParams {
  hiddenCount: number;
  onToggleExpand: () => void;
}

export interface OverflowPillRowsProps<T> {
  collapsedMaxRows?: number;
  enablePopover?: boolean;
  expandLabel?: string;
  gap?: string;
  getItemKey?: (item: T, index: number) => string;
  isExpanded: boolean;
  items: T[];
  maxRows?: number;
  onToggleExpand: () => void;
  popoverPlacement?: 'top' | 'bottom';
  renderExpandControl?: (params: OverflowPillExpandControlParams) => ReactNode;
  renderPill: (item: T, context: OverflowPillRenderContext) => ReactNode;
  renderPopoverContent?: (items: T[]) => ReactNode;
  renderPopoverTrigger?: (overflowCount: number) => ReactNode;
  reservePopoverSlot?: boolean;
  rowGap?: string;
  showLessLabel: string;
}

const defaultGetItemKey = <T,>(_item: T, index: number): string =>
  `overflow-pill-${index}`;

const DEFAULT_COLLAPSED_MAX_ROWS = 1;
const DEFAULT_MAX_ROWS = 2;

export const OverflowPillRows = <T,>({
  collapsedMaxRows = DEFAULT_COLLAPSED_MAX_ROWS,
  enablePopover = true,
  expandLabel,
  gap = '8px',
  getItemKey = defaultGetItemKey,
  isExpanded,
  items,
  maxRows = DEFAULT_MAX_ROWS,
  onToggleExpand,
  popoverPlacement = 'top',
  renderExpandControl,
  renderPill,
  renderPopoverContent,
  renderPopoverTrigger,
  reservePopoverSlot = true,
  rowGap = gap,
  showLessLabel,
}: OverflowPillRowsProps<T>) => {
  const activeMaxRows = isExpanded ? maxRows : collapsedMaxRows;
  const layout = useOverflowPillLayout({
    gap,
    itemCount: items.length,
    maxRows: activeMaxRows,
    reserveControlSlot: reservePopoverSlot,
  });

  const measuredVisibleItemIndexes = layout.hasMeasured
    ? layout.visibleItemIndexes
    : items.length > 0
      ? [0]
      : [];

  const visibleItemIndexes =
    !enablePopover && isExpanded
      ? items.map((_, index) => index)
      : measuredVisibleItemIndexes;

  const slices = sliceOverflowPillItems({
    isExpanded,
    items,
    visibleItemIndexes,
  });

  const collapsedHiddenCount = layout.hasMeasured
    ? Math.max(items.length - layout.visibleItemIndexes.length, 0)
    : 0;

  const popoverItemIndexes = items
    .map((_, index) => index)
    .filter(index => !layout.visibleItemIndexes.includes(index));

  const revealStartIndexRef = useRef(0);

  useLayoutEffect(() => {
    if (!isExpanded) {
      revealStartIndexRef.current = layout.visibleItemIndexes.length;
    }
  }, [isExpanded, layout.visibleItemIndexes.length]);

  const renderPillItem = (item: T, index: number) => (
    <PillCell key={getItemKey(item, index)} data-overflow-pill-index={index}>
      {renderPill(item, {
        index,
        isRevealedOnExpand: isExpanded && index >= revealStartIndexRef.current,
      })}
    </PillCell>
  );

  const defaultPopoverTrigger = (overflowCount: number) => (
    <OverflowCountTrigger type="button">+{overflowCount}</OverflowCountTrigger>
  );

  const popoverTrigger = renderPopoverTrigger ?? defaultPopoverTrigger;

  const popoverContent =
    renderPopoverContent ??
    ((popoverItems: T[]) => (
      <OverflowPopover>
        {popoverItems.map((item, popoverIndex) => {
          const globalIndex = popoverItemIndexes[popoverIndex] ?? popoverIndex;

          return (
            <Fragment key={getItemKey(item, globalIndex)}>
              {renderPill(item, {
                index: globalIndex,
                isRevealedOnExpand: false,
              })}
            </Fragment>
          );
        })}
      </OverflowPopover>
    ));

  const renderMeasurePillItem = (item: T, index: number) => (
    <PillCell
      key={`measure-${getItemKey(item, index)}`}
      data-overflow-pill-index={index}
    >
      {renderPill(item, {
        index,
        isRevealedOnExpand: false,
      })}
    </PillCell>
  );

  const renderControlMeasurePlaceholder = (hiddenCount: number) => (
    <TrailingControlCell
      aria-hidden
      data-hidden-count={hiddenCount}
      data-overflow-control-measure
      key={`measure-control-${hiddenCount}`}
    >
      <ControlMeasurePlaceholder>+{hiddenCount}</ControlMeasurePlaceholder>
    </TrailingControlCell>
  );

  const renderPopoverControl = () =>
    enablePopover && slices.showPopoverControl ? (
      <TrailingControlCell data-overflow-trailing-control>
        <PopperComponent
          placement={popoverPlacement}
          visibleElement={
            <PopoverTriggerSlot>
              {popoverTrigger(slices.popoverOverflowCount)}
            </PopoverTriggerSlot>
          }
          PopperContent={
            <OverflowPopoverContent>
              {popoverContent(slices.popoverItems)}
            </OverflowPopoverContent>
          }
        />
      </TrailingControlCell>
    ) : null;

  const renderExpandControlInTrack = () =>
    !isExpanded && layout.hasMeasured && slices.showExpandControl ? (
      <TrailingControlCell data-overflow-trailing-control>
        {renderExpandControl?.({
          hiddenCount: collapsedHiddenCount,
          onToggleExpand,
        }) ?? (
          <ExpandButton type="button" onClick={onToggleExpand}>
            {expandLabel ?? `+${collapsedHiddenCount}`}
          </ExpandButton>
        )}
      </TrailingControlCell>
    ) : null;

  const renderPillIndexes = (indexes: number[]) =>
    indexes.map(index => {
      const item = items[index];

      if (item === undefined) {
        return null;
      }

      return renderPillItem(item, index);
    });

  const controlHiddenCounts = Array.from(
    { length: items.length },
    (_, index) => items.length - index,
  ).filter(hiddenCount => hiddenCount > 0);

  return (
    <Layout $gap={gap}>
      <TrackShell>
        <PillsTrack ref={layout.trackRef} $gap={gap} $rowGap={rowGap}>
          {renderPillIndexes(visibleItemIndexes)}
          {isExpanded ? renderPopoverControl() : renderExpandControlInTrack()}
        </PillsTrack>
        <HiddenMeasureTrack
          ref={layout.measureRef}
          $gap={gap}
          $rowGap={rowGap}
          aria-hidden
        >
          {items.map((item, index) => renderMeasurePillItem(item, index))}
          {controlHiddenCounts.map(hiddenCount =>
            renderControlMeasurePlaceholder(hiddenCount),
          )}
        </HiddenMeasureTrack>
      </TrackShell>
      {isExpanded && slices.showShowLessControl && (
        <ShowLessButton type="button" onClick={onToggleExpand}>
          {showLessLabel}
        </ShowLessButton>
      )}
    </Layout>
  );
};

const Layout = styled('div')<{ $gap: string }>`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap};
  min-width: 0;
  width: 100%;
  margin-bottom: 8px;
`;

const TrackShell = styled('div')`
  position: relative;
  width: 100%;
`;

const PillsTrack = styled('div', forwardRef)<{
  $gap: string;
  $rowGap: string;
}>`
  align-content: flex-start;
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $gap }) => $gap};
  max-width: 100%;
  min-width: 0;
  overflow: visible;
  padding: 4px 0;
  row-gap: ${({ $rowGap }) => $rowGap};
  width: 100%;
`;

const HiddenMeasureTrack = styled(PillsTrack, forwardRef)`
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  visibility: hidden;
  width: 100%;
`;

const TrailingControlCell = styled('div')`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-shrink: 0;
`;

const ControlMeasurePlaceholder = styled('span')`
  ${({ theme }) => theme.typography.spotlight.toggleButton}
  background: ${({ theme }) => theme.colors.spotlight.surface.accent};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.surface.accentHover};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  flex-shrink: 0;
  padding: 6px 10px;
  white-space: nowrap;
`;

const PillCell = styled('div')`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  max-width: 180px;
  min-height: max-content;
  min-width: 0;
  overflow: hidden;
  width: max-content;
  max-width: 180px;
`;

const ExpandButton = styled('button')`
  background: transparent;
  border: 0;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  cursor: pointer;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  ${({ theme }) => theme.typography.spotlight.sectionLabel}
  text-decoration: none;
  text-transform: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.spotlight.border.focus};
    outline-offset: 1px;
  }
`;

const ShowLessButton = styled('button')`
  background: transparent;
  border: 0;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  cursor: pointer;
  margin: 0;
  padding: 0;
  text-align: left;
  ${({ theme }) => theme.typography.spotlight.sectionLabel}
  text-decoration: none;
  text-transform: none;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.spotlight.border.focus};
    outline-offset: 1px;
  }
`;

const OverflowCountTrigger = styled('button')`
  ${({ theme }) => theme.typography.spotlight.toggleButton}
  background: ${({ theme }) => theme.colors.spotlight.surface.accent};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.surface.accentHover};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  cursor: pointer;
  flex-shrink: 0;
  padding: 6px 10px;
  white-space: nowrap;
  ${({ theme }) =>
    theme.mixins.transition(
      ['background-color'],
      theme.duration.normal,
      theme.easing.out,
    )}

  &:hover {
    background: ${({ theme }) => theme.colors.spotlight.surface.accentHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.spotlight.border.focus};
    outline-offset: 1px;
  }
`;

const PopoverTriggerSlot = styled('span')`
  display: inline-flex;
  flex-shrink: 0;
`;

const OverflowPopoverContent = styled('div')``;

const OverflowPopover = styled('div')`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.spotlight.popover};
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 6px;
  max-width: 240px;
  padding: 12px;

  & > * {
    display: block;
    flex-shrink: 0;
  }
`;
